import type { SedrRequirement } from '../../api/sedr';
import { applyI18n, t } from '../../i18n';
import { enhanceDataTableCards } from '../../ui/dataTableCards';
import { bindModalDismiss, createModalShell } from '../../ui/modalDialogShell';
import { statusBadgeClass, tSedrStatus } from './sedrUi';

export interface SedrSupersedeTargetDialogOptions {
  candidates: SedrRequirement[];
}

/**
 * Modal single-select picker for a superseding requirement.
 * Returns the selected requirement id, or null if cancelled.
 */
export async function showSedrSupersedeTargetDialog(
  input: SedrSupersedeTargetDialogOptions,
): Promise<string | null> {
  const sorted = [...input.candidates].sort((a, b) =>
    a.requirementCode.localeCompare(b.requirementCode, undefined, { sensitivity: 'base' }),
  );

  return new Promise((resolve) => {
    const shell = createModalShell({
      titleKey: 'sedr.drawer.supersedeTargetTitle',
      className: 'sedr-supersede-target-dialog',
    });

    shell.body.innerHTML = `
      <p class="muted" data-i18n="sedr.drawer.supersedeTargetHint"></p>
      <div class="sedr-supersede-target">
        <div class="sedr-supersede-target__toolbar">
          <input
            type="search"
            class="files-explorer-bar__search sedr-supersede-target__search"
            id="sedr-supersede-search"
            data-i18n-placeholder="sedr.drawer.supersedeTargetSearch"
          />
        </div>
        <div class="files-table-wrap sedr-supersede-target__table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th data-i18n="sedr.table.reqId"></th>
                <th data-i18n="sedr.table.title"></th>
                <th data-i18n="sedr.table.status"></th>
              </tr>
            </thead>
            <tbody id="sedr-supersede-tbody"></tbody>
          </table>
          <p class="muted sedr-supersede-target__empty" id="sedr-supersede-empty" hidden data-i18n="sedr.drawer.supersedeTargetEmpty"></p>
        </div>
        <div class="sedr-supersede-target__selection" id="sedr-supersede-selection" aria-live="polite"></div>
      </div>
    `;

    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.className = 'button ghost';
    cancelButton.id = 'sedr-supersede-cancel';
    cancelButton.innerHTML = '<span data-i18n="common.cancel"></span>';

    const confirmButton = document.createElement('button');
    confirmButton.type = 'button';
    confirmButton.className = 'button primary';
    confirmButton.id = 'sedr-supersede-confirm';
    confirmButton.disabled = true;
    confirmButton.innerHTML = '<span data-i18n="sedr.drawer.supersedeTargetConfirm"></span>';

    shell.actions.append(cancelButton, confirmButton);
    document.body.append(shell.backdrop);
    applyI18n(shell.dialog);

    let selectedId: string | null = null;
    let searchQuery = '';

    const table = shell.body.querySelector('table.data-table') as HTMLTableElement;
    const tbody = shell.body.querySelector('#sedr-supersede-tbody') as HTMLTableSectionElement;
    const emptyEl = shell.body.querySelector('#sedr-supersede-empty') as HTMLParagraphElement;
    const selectionEl = shell.body.querySelector('#sedr-supersede-selection') as HTMLElement;
    const searchInput = shell.body.querySelector('#sedr-supersede-search') as HTMLInputElement;
    const byId = new Map(sorted.map((r) => [r.id, r]));

    const finish = (value: string | null) => {
      unbindDismiss();
      shell.backdrop.remove();
      resolve(value);
    };

    const unbindDismiss = bindModalDismiss({
      backdrop: shell.backdrop,
      closeButton: shell.closeButton,
      onClose: () => finish(null),
    });

    const updateSelectionChrome = () => {
      confirmButton.disabled = !selectedId;
      selectionEl.replaceChildren();

      if (!selectedId) {
        selectionEl.className = 'sedr-supersede-target__selection sedr-supersede-target__selection--empty';
        const label = document.createElement('span');
        label.className = 'muted';
        label.dataset.i18n = 'sedr.drawer.supersedeTargetNoneSelected';
        label.textContent = t('sedr.drawer.supersedeTargetNoneSelected');
        selectionEl.append(label);
        applyI18n(selectionEl);
      } else {
        const req = byId.get(selectedId);
        selectionEl.className = 'sedr-supersede-target__selection';
        const caption = document.createElement('span');
        caption.className = 'sedr-supersede-target__selection-label';
        caption.dataset.i18n = 'sedr.drawer.supersedeTargetSelected';
        caption.textContent = t('sedr.drawer.supersedeTargetSelected');
        selectionEl.append(caption);

        const detail = document.createElement('span');
        detail.className = 'sedr-supersede-target__selection-detail';
        detail.textContent = req
          ? `${req.requirementCode} — ${req.title}`
          : selectedId;
        selectionEl.append(detail);
        applyI18n(selectionEl);
      }

      for (const row of tbody.querySelectorAll('.sedr-supersede-target__row')) {
        const id = row.getAttribute('data-requirement-id');
        row.classList.toggle('sedr-supersede-target__row--selected', id === selectedId);
      }
    };

    const selectRequirement = (id: string) => {
      selectedId = id;
      updateSelectionChrome();
    };

    const refreshListing = () => {
      const query = searchQuery.trim().toLowerCase();
      const visible = query
        ? sorted.filter(
            (r) =>
              r.requirementCode.toLowerCase().includes(query) ||
              r.title.toLowerCase().includes(query),
          )
        : sorted;

      tbody.replaceChildren();

      for (const req of visible) {
        const tr = document.createElement('tr');
        tr.className = 'sedr-supersede-target__row';
        tr.dataset.requirementId = req.id;
        tr.tabIndex = 0;

        const codeCell = document.createElement('td');
        const codeBtn = document.createElement('button');
        codeBtn.type = 'button';
        codeBtn.className = 'files-table__name-button';
        codeBtn.textContent = req.requirementCode;
        codeCell.append(codeBtn);
        tr.append(codeCell);

        const titleCell = document.createElement('td');
        titleCell.textContent = req.title;
        tr.append(titleCell);

        const statusCell = document.createElement('td');
        const badge = document.createElement('span');
        badge.className = statusBadgeClass(req.status);
        badge.textContent = tSedrStatus(req.status);
        statusCell.append(badge);
        tr.append(statusCell);

        const activate = () => selectRequirement(req.id);
        const confirmSelection = () => {
          selectedId = req.id;
          finish(req.id);
        };

        codeBtn.addEventListener('click', activate);
        codeBtn.addEventListener('dblclick', confirmSelection);
        tr.addEventListener('click', (e) => {
          if ((e.target as HTMLElement).closest('button')) return;
          activate();
        });
        tr.addEventListener('dblclick', confirmSelection);
        tr.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            confirmSelection();
          }
        });

        tbody.append(tr);
      }

      emptyEl.hidden = visible.length > 0;
      enhanceDataTableCards(table);
      applyI18n(shell.dialog);
      updateSelectionChrome();
    };

    searchInput.addEventListener('input', () => {
      searchQuery = searchInput.value;
      refreshListing();
    });

    cancelButton.addEventListener('click', () => finish(null));
    confirmButton.addEventListener('click', () => {
      if (selectedId) finish(selectedId);
    });

    refreshListing();
    searchInput.focus();
  });
}
